import { FileText, Download, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { VehicleTest } from "@/types/vehicleTest";
import { useLanguage } from "@/context/LanguageContext";
import { vehicleCardUILabels } from "@/i18n";

interface VehicleCardTestSectionCardProps {
  testData: VehicleTest[] | undefined;
  statusFilter: (test: VehicleTest) => boolean;
  title: string;
  iconColor: string;
  notFoundMessage: string;
  dateLabel?: string;
  registrationNumberLabel?: string;
  viewTestProtocolTitle?: string;
  downloadPdfTitle?: string;
  onViewTest: (test: VehicleTest) => void;
  onDownloadTestPdf: (test: VehicleTest) => void;
  isTestGeneratingPdf: (testId: string) => boolean;
}

export const VehicleCardTestSectionCard = ({
  testData,
  statusFilter,
  title,
  iconColor,
  notFoundMessage,
  dateLabel,
  registrationNumberLabel,
  viewTestProtocolTitle,
  downloadPdfTitle,
  onViewTest,
  onDownloadTestPdf,
  isTestGeneratingPdf,
}: VehicleCardTestSectionCardProps) => {
  const { language } = useLanguage();
  const vcLabels = vehicleCardUILabels(language);

  // Use i18n labels as defaults if not provided
  const finalDateLabel = dateLabel || vcLabels.date;
  const finalRegistrationNumberLabel =
    registrationNumberLabel || vcLabels.registrationNumberFull;
  const finalViewTestProtocolTitle =
    viewTestProtocolTitle || vcLabels.viewTestProtocol;
  const finalDownloadPdfTitle = downloadPdfTitle || vcLabels.downloadPdf;

  // Ensure testData is always an array
  const safeTestData = Array.isArray(testData) ? testData : [];
  const filteredTests = safeTestData.filter(statusFilter);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {/* Render filtered tests */}
        {filteredTests.map((test) => (
          <Card key={test.id} className="shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className={`h-5 w-5 ${iconColor}`} />
                  <div>
                    <h4 className="font-medium">{title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {finalDateLabel}:{" "}
                      {new Date(test.date).toLocaleDateString(
                        language === "en" ? "en-GB" : "sv-SE",
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {finalRegistrationNumberLabel}:{" "}
                      {test.vehicleDetails?.regNumber || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewTest(test)}
                    title={finalViewTestProtocolTitle}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDownloadTestPdf(test)}
                    title={finalDownloadPdfTitle}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Show empty state */}
      {filteredTests.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          {notFoundMessage}
        </div>
      )}
    </div>
  );
};
